// Maps form values onto the BooleanSearchRequest body using each field's
// configured booleanApiKey. The enum mirrors the Go struct in
// Be_Auth_DevOps models/boolean_search.go, which this frontend cannot change.
//
// Starred-chip fields (type: 'keywords') are the exception: they are collected
// once, after the loop, into the single `keywordGroups` parameter — grouping is
// a whole-form operation because two config fields can feed one group (an
// "Exclude Keywords" box attaches to the Keywords box by naming the same
// keywordGroup). See Be_Auth_DevOps/docs/keyword-search-api-request-contract.md.
import {
  buildKeywordGroups,
  isKeywordField,
  isPrimaryKeywordField,
  normalizeKeywordValue,
} from './keywordCriteria';

export const BOOLEAN_API_KEYS = [
  'query',
  'skills',
  'jobTitle',
  'designation',
  'currentLocation',
  'experience.from',
  'experience.to',
  'workExperienceYears',
  'sourceType',
  // Sent as a pair by a daterangePreset field. The Go BooleanSearchRequest does
  // not declare these yet, so they are accepted and ignored server-side (Go's
  // json.Decode drops unknown keys) until the boolean-search API adds them.
  'createdFrom',
  'createdTo',
];

const NUMERIC_KEYS = new Set(['experience.from', 'experience.to', 'workExperienceYears']);

function setDeep(body, key, value) {
  if (!key.includes('.')) {
    body[key] = value;
    return;
  }
  const [root, leaf] = key.split('.');
  body[root] = { ...(body[root] ?? {}), [leaf]: value };
}

function unwrap(raw) {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return raw.value ?? raw.label ?? raw.name ?? '';
  }
  return raw;
}

function coerce(key, input) {
  const raw = unwrap(input);

  if (NUMERIC_KEYS.has(key)) {
    // Number('') is 0, and 0 is finite — without this guard an absent value (or
    // an object unwrap that yielded '') would be sent as a real bound of zero.
    if (raw === '' || raw === null || raw === undefined) return undefined;
    const numeric = Number(raw);
    return Number.isFinite(numeric) ? numeric : undefined;
  }

  if (Array.isArray(raw)) {
    const parts = raw
      .map(unwrap)
      .filter((part) => part !== undefined && part !== null && String(part).trim() !== '');
    if (!parts.length) return undefined;
    // sourceType is a comma list server-side; every other string key is parsed
    // as a boolean expression, so multi-select values become an OR group.
    return key === 'sourceType' ? parts.join(',') : parts.join(' OR ');
  }

  const text = String(raw ?? '').trim();
  return text === '' ? undefined : text;
}

export function buildBooleanRequest(values = {}, fields = [], options = {}) {
  const body = {};
  const booleanMode = Boolean(options.booleanMode);

  fields.forEach((field) => {
    const apiKey = field.booleanApiKey;
    if (!apiKey) return;

    let raw = values[field.field];

    if (isKeywordField(field)) {
      const model = normalizeKeywordValue(raw);
      // In Boolean mode the primary field IS the expression: it fills `query`
      // and contributes no group. Every other keyword field keeps its chips, so
      // Skills still filters while the common box is in expression mode.
      if (isPrimaryKeywordField(field)) {
        if (!booleanMode) return;
        const expression = model.expression.trim();
        if (expression) setDeep(body, Array.isArray(apiKey) ? apiKey[0] : apiKey, expression);
        return;
      }
      // Deprecated legacy mirror. `keywordGroups` below is the real contract,
      // but until the backend reads it a converted field must keep sending
      // exactly what it sent as a tagify — no better, no worse — so switching
      // Skills to starred chips changes nothing about today's results.
      // Remove once keywordGroups ships server-side.
      raw = model.terms.map((term) => term.value);
    }

    if (raw === undefined || raw === null || raw === '') return;

    // A composite control (range slider, date range) maps one value tuple onto
    // several request params. A range slider already holds a [from, to] array;
    // a daterangePreset holds {preset, from, to}, so unpack it in the same order
    // as its configured key pair.
    if (Array.isArray(apiKey)) {
      let parts;
      if (Array.isArray(raw)) {
        parts = raw;
      } else if (raw && typeof raw === 'object' && ('from' in raw || 'to' in raw)) {
        parts = [raw.from, raw.to];
      } else {
        parts = [raw];
      }
      apiKey.forEach((key, index) => {
        const value = coerce(key, parts[index]);
        if (value !== undefined) setDeep(body, key, value);
      });
      return;
    }

    const value = coerce(apiKey, raw);
    if (value !== undefined) setDeep(body, apiKey, value);
  });

  // Only when the caller supplies one. This used to fall back to 'internal',
  // which is Candidates vocabulary — a Jobs or Clients search form has no source
  // picker at all, and sending it a sourceType it never configured is noise the
  // backend has to ignore. The caller knows whether its form has the field.
  if (!body.sourceType && options.defaultSourceType) {
    body.sourceType = options.defaultSourceType;
  }

  // Collected once for the whole form rather than per field, so two config
  // fields naming the same keywordGroup arrive merged. Attached only when
  // something survives, so a form with no starred-chip field sends a payload
  // byte-identical to before this existed.
  const keywordGroups = buildKeywordGroups(fields, values, { booleanMode });
  if (keywordGroups) body.keywordGroups = keywordGroups;

  // Which module's config answers for this search — the same key the traditional
  // endpoint receives as ?module=, so both search modes resolve their columns,
  // row actions and rules from one place. Omitted when the caller supplies none,
  // so an existing form's payload is byte-identical to before.
  if (options.module) body.module = String(options.module);

  return body;
}
